/**
 * Base Column Definitions
 * Common columns used across all service detail tables
 */
import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createOptionsFormatter,
    createCurrencyFormatter,
    createDateFormatter,
    createTimeRangeFormatter,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import type { ServiceDetailRow } from './types'
import ServiceContract from '../../components/Detail/Templates/shared/ServiceContract'

// ============================================================================
// SERVICE STATUS OPTIONS
// ============================================================================

export const SERVICE_STATUS_OPTIONS = (t: TFunction): OptionItem[] => [
    {
        label: t('service-status.waiting-confirm'),
        value: 'waiting-confirm',
        color: 'info',
    },
    {
        label: t('service-status.waiting-create-service'),
        value: 'waiting-create-service',
        color: 'warning',
    },
    {
        label: t('service-status.trialing'),
        value: 'trialing',
        color: 'primary',
    },
    { label: t('service-status.active'), value: 'active', color: 'success' },
    {
        label: t('service-status.on-resubmit-information'),
        value: 'on-resubmit-information',
        color: 'warning',
    },
    {
        label: t('service-status.pending-terminated'),
        value: 'pending-terminated',
        color: 'error',
    },
    {
        label: t('service-status.cancelled'),
        value: 'cancelled',
        color: 'error',
    },
    { label: t('service-status.rejected'), value: 'rejected', color: 'error' },
]

// ============================================================================
// BASE COLUMNS (service name, status)
// ============================================================================

export const createBaseColumns = (
    t: TFunction
): DataTableColumn<ServiceDetailRow>[] => [
    {
        key: 'service_name',
        title: t('service_name'),
        width: 250,
        pinned: 'left',
        render: (value, row) => {
            const displayName = (value || row.service_id || '')
                ?.toString()
                ?.toUpperCase()
            if (row._rowType === 'total') {
                return (
                    <span className="font-medium text-primary">
                        {t('total', { defaultValue: 'Tổng cộng' })}
                    </span>
                )
            }
            if (row._rowType === 'addon') {
                return (
                    <span className="text-xs inline-flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-primary" />
                        {displayName || t('addon', { defaultValue: 'Addon' })}
                    </span>
                )
            }
            return displayName
        },
    },
    {
        key: 'name',
        title: t('entity_name', { defaultValue: 'Portal/Domain/IP' }),
        width: 180,
        render: (value, row) => {
            if (row._rowType === 'total') return null
            return value || '—'
        },
    },
    {
        key: 'service_status',
        title: t('service_status'),
        width: 130,
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return undefined
        },
        formatter: createOptionsFormatter({
            options: SERVICE_STATUS_OPTIONS(t),
        }),
    },
]

// ============================================================================
// PRICE COLUMNS
// ============================================================================

export const createPriceColumns = (
    t: TFunction
): DataTableColumn<ServiceDetailRow>[] => [
    {
        key: 'service_price_register',
        title: t('service_price_register'),
        width: 130,
        cellClassName: 'text-right',
        render: (value, row) => {
            if (row._rowType === 'total' && value) {
                return (
                    <span className="font-medium text-primary">
                        {new Intl.NumberFormat('en-US').format(value)}
                    </span>
                )
            }
            return undefined
        },
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'service_price_recurring',
        title: t('service_price_recurring'),
        width: 130,
        cellClassName: 'text-right',
        render: (value, row) => {
            if (row._rowType === 'total' && value) {
                return (
                    <span className="font-medium text-primary">
                        {new Intl.NumberFormat('en-US').format(value)}
                    </span>
                )
            }
            return undefined
        },
        formatter: createCurrencyFormatter({ currency: '' }),
    },
]

// ============================================================================
// DATE COLUMNS
// ============================================================================

export const createDateColumns = (
    t: TFunction
): DataTableColumn<ServiceDetailRow>[] => [
    {
        key: 'employee_names',
        title: t('employee_sale_tracking_ids'),
        width: 200,
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return row?.employee_names?.join(', ') || '-'
        },
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 110,
        cellClassName: 'text-right',
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return undefined
        },
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_active_cycling',
        title: t('service_active_cycling'),
        width: 180,
        cellClassName: 'text-right',
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return undefined
        },
        formatter: createTimeRangeFormatter({
            startKey: 'current_startdate',
            endKey: 'current_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true,
        }),
    },
    {
        key: 'service_renew_cycling',
        title: t('service_renew_cycling'),
        width: 180,
        cellClassName: 'text-right',
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return undefined
        },
        formatter: createTimeRangeFormatter({
            startKey: 'continue_startdate',
            endKey: 'continue_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true,
        }),
    },
    {
        key: 'service_stopped_date',
        title: t('service_stopped_date'),
        width: 110,
        cellClassName: 'text-right',
        render: (_, row) => {
            if (row._rowType === 'total') return null
            return undefined
        },
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

// ============================================================================
// CONTRACT COLUMN
// ============================================================================

export const createContractColumn = (
    t: TFunction
): DataTableColumn<ServiceDetailRow> => ({
    key: 'service_contract',
    title: t('service_contract'),
    width: 300,
    render: (_, row) => {
        if (row._rowType === 'total' || !row.service_id) return null
        return <ServiceContract serviceID={row.service_id} />
    },
})
